import { offlineStore } from './OfflineStore';
import { LibraryDB } from '../services/LibraryDB';

interface DownloadStatus {
    bookId: string;
    state: 'idle' | 'downloading' | 'paused' | 'completed' | 'error';
    progress: number; // 0-100
    totalChunks: number;
    downloadedChunks: number;
    error?: string;
}

type Listener = (status: DownloadStatus) => void;

class DownloadManager {
    private queue: Map<string, DownloadStatus> = new Map();
    private controllers: Map<string, AbortController> = new Map();
    private listeners: Set<Listener> = new Set();

    constructor() {}

    addListener(l: Listener) { this.listeners.add(l); }
    removeListener(l: Listener) { this.listeners.delete(l); }
    private notify(s: DownloadStatus) {
        this.queue.set(s.bookId, s);
        this.listeners.forEach(l => l(s));
    }

    async getStatus(bookId: string): Promise<DownloadStatus> {
        if (this.queue.has(bookId)) return this.queue.get(bookId)!;

        // Check local DB status
        const book = await LibraryDB.getBook(bookId);
        if (book?.audioDownloadState === 'downloaded_verified') {
            return {
                bookId, state: 'completed', progress: 100, totalChunks: 0, downloadedChunks: 0
            };
        } else if (book?.audioDownloadState === 'failed') {
            return {
                bookId, state: 'error', progress: book.audioDownloadProgress || 0, totalChunks: 0, downloadedChunks: 0, error: 'Verification failed'
            };
        } else if (book?.audioDownloadState === 'partial') {
            return {
                bookId, state: 'paused', progress: book.audioDownloadProgress || 0, totalChunks: 0, downloadedChunks: 0
            };
        } else if (book?.audioDownloadState === 'downloading') {
            return {
                bookId, state: 'downloading', progress: book.audioDownloadProgress || 0, totalChunks: 0, downloadedChunks: 0
            };
        }

        return {
            bookId, state: 'idle', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
    }

    async startDownload(bookId: string, playlistUrl: string) {
        if (this.queue.get(bookId)?.state === 'downloading') return;

        const controller = new AbortController();
        this.controllers.set(bookId, controller);

        // Get initial progress if resuming a partial download
        const currentBook = await LibraryDB.getBook(bookId);
        const initialProgress = currentBook?.audioDownloadProgress || 0;

        const status: DownloadStatus = {
            bookId, state: 'downloading', progress: initialProgress, totalChunks: 0, downloadedChunks: 0
        };
        this.notify(status);
        await LibraryDB.updateAudioDownloadFields(bookId, {
            audioDownloadState: 'downloading',
            audioDownloadProgress: initialProgress
        });

        try {
            // 0. Fetch and save cover if available
            const book = await LibraryDB.getBook(bookId);
            if (book?.coverUrl) {
                try {
                    const existingCover = await offlineStore.getChunk(bookId, 'cover.webp');
                    if (!existingCover || existingCover.size === 0) {
                        const coverRes = await fetch(book.coverUrl, { signal: controller.signal });
                        if (coverRes.ok) {
                            const coverBlob = await coverRes.blob();
                            await offlineStore.saveChunk(bookId, 'cover.webp', coverBlob, 'pinned');
                        }
                    }
                } catch (coverErr) {
                    console.error("[DownloadManager] Failed to download cover", coverErr);
                }
            }

            // 1. Fetch Playlist
            const res = await fetch(playlistUrl);
            if (!res.ok) throw new Error('Failed to fetch playlist');
            const data = await res.json();
            const tracks = data.tracks.filter((t: any) => t.url && t.url.match(/\.(mp3|m4a)$/i));

            try {
                const playlistBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                await offlineStore.saveChunk(bookId, 'playlist.json', playlistBlob, 'pinned');
            } catch { }

            if (!tracks || tracks.length === 0) {
                throw new Error("Download aborted: Playlist has no valid audio tracks.");
            }

            status.totalChunks = tracks.length;
            this.notify({ ...status });

            // 2. Download loop
            const concurrency = 3;
            const chunks = [...tracks];
            let completed = 0;

            const processChunk = async (track: any) => {
                if (controller.signal.aborted) return;

                let filename: string;
                if (track.key && /\.(mp3|m4a|wav|ogg)$/i.test(track.key)) {
                    filename = track.key.split('/').pop() || 'audio.mp3';
                } else {
                    const urlPath = track.url.split('?')[0];
                    filename = urlPath.split('/').pop() || 'audio.mp3';
                }

                // Check if already exists and is non-empty
                const existing = await offlineStore.getChunk(bookId, filename);
                if (existing && existing.size > 0) {
                    completed++;
                    status.downloadedChunks = completed;
                    status.progress = Math.floor((completed / status.totalChunks) * 100);
                    this.notify({ ...status });
                    await LibraryDB.updateAudioDownloadFields(bookId, {
                        audioDownloadProgress: status.progress
                    });
                    return;
                }

                // Fetch
                try {
                    const r = await fetch(track.url, { signal: controller.signal });
                    if (!r.ok) throw new Error('Fetch failed');
                    const blob = await r.blob();
                    await offlineStore.saveChunk(bookId, filename, blob, 'pinned');

                    completed++;
                    status.downloadedChunks = completed;
                    status.progress = Math.floor((completed / status.totalChunks) * 100);
                    this.notify({ ...status });
                    await LibraryDB.updateAudioDownloadFields(bookId, {
                        audioDownloadProgress: status.progress
                    });
                } catch (e: any) {
                    if (e.name === 'AbortError') throw e;
                    console.error(`Chunk failed ${filename}`, e);
                }
            };

            for (let i = 0; i < chunks.length; i += concurrency) {
                if (controller.signal.aborted) break;
                const batch = chunks.slice(i, i + concurrency);
                await Promise.all(batch.map(processChunk));
            }

            if (controller.signal.aborted) return;

            // Verification Pass
            const verified = await this.verifyDownload(bookId);
            if (verified) {
                status.state = 'completed';
                status.progress = 100;
                this.notify({ ...status });
                await LibraryDB.updateAudioDownloadFields(bookId, {
                    audioDownloadState: 'downloaded_verified',
                    audioDownloadProgress: 100,
                    audioVerifiedTimestamp: Date.now()
                });
            } else {
                throw new Error("Verification failed. Some files are missing or empty.");
            }

        } catch (e: any) {
            if (e.name === 'AbortError') {
                status.state = 'paused';
                this.notify({ ...status });
                await LibraryDB.updateAudioDownloadFields(bookId, {
                    audioDownloadState: 'partial'
                });
            } else {
                status.state = 'error';
                status.error = e.message;
                this.notify({ ...status });
                await LibraryDB.updateAudioDownloadFields(bookId, {
                    audioDownloadState: 'failed'
                });
            }
        } finally {
            this.controllers.delete(bookId);
        }
    }

    async verifyDownload(bookId: string): Promise<boolean> {
        try {
            // 1. Get playlist.json
            const playlistChunk = await offlineStore.getChunk(bookId, 'playlist.json');
            if (!playlistChunk) {
                console.error("[verifyDownload] Missing playlist.json");
                return false;
            }
            const playlistText = await playlistChunk.text();
            const playlistData = JSON.parse(playlistText);
            const tracks = playlistData.tracks || [];
            if (tracks.length === 0) {
                console.error("[verifyDownload] Playlist has no tracks");
                return false;
            }

            // 2. Check each track chunk exists and is non-empty
            for (const track of tracks) {
                if (!track.url) continue;

                let filename: string;
                if (track.key && /\.(mp3|m4a|wav|ogg)$/i.test(track.key)) {
                    filename = track.key.split('/').pop() || 'audio.mp3';
                } else {
                    const urlPath = track.url.split('?')[0];
                    filename = urlPath.split('/').pop() || 'audio.mp3';
                }

                const chunkBlob = await offlineStore.getChunk(bookId, filename);
                if (!chunkBlob || chunkBlob.size === 0) {
                    console.error(`[verifyDownload] Missing or empty chunk: ${filename}`);
                    return false;
                }
            }

            // 3. Confirm cover exists if book has coverUrl
            const book = await LibraryDB.getBook(bookId);
            if (book?.coverUrl) {
                const coverBlob = await offlineStore.getChunk(bookId, 'cover.webp');
                if (!coverBlob || coverBlob.size === 0) {
                    console.error("[verifyDownload] Missing or empty cover.webp");
                    return false;
                }
            }

            return true;
        } catch (err) {
            console.error("[verifyDownload] Exception during verification", err);
            return false;
        }
    }

    pauseDownload(bookId: string) {
        const c = this.controllers.get(bookId);
        if (c) c.abort();
    }

    async removeDownload(bookId: string) {
        this.pauseDownload(bookId);
        await offlineStore.removeBook(bookId);
        const s: DownloadStatus = {
            bookId, state: 'idle', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
        this.notify(s);
        await LibraryDB.updateAudioDownloadFields(bookId, {
            audioDownloadState: 'not_downloaded',
            audioDownloadProgress: 0,
            audioVerifiedTimestamp: undefined
        });
    }
}

export const downloadManager = new DownloadManager();
